
import React, { useRef, useEffect } from 'react'
import AvatarImage from '../utils/AvatarImage'
import { MessageTypes } from '../constants/index'
import { composeClassName } from '../utils/common'
import { MessageProps } from '../../MessageDisplayArea'
import { PreviewMessage } from '../models'

export interface LinkPreviewProps extends Omit<MessageProps, 'message'> {
  message: PreviewMessage;
}

const MIN_WIDTH = 50;
const MIN_HEIGHT = 50;
const SMALL_WIDTH = 240;
const MEDIUM_WIDTH = 300;
const BIG_WIDTH = 400;
const BIG_SCREEN_BORDER = 510;
const SMALL_SCREEN_BORDER = 400;
const GET_CLIENT_WIDTH = () => window.document.documentElement.clientWidth;
const IS_SMALL_SCREEN = () => GET_CLIENT_WIDTH() < SMALL_SCREEN_BORDER;
const IS_MEDIUM_SCREEN = () => GET_CLIENT_WIDTH() > SMALL_SCREEN_BORDER && GET_CLIENT_WIDTH() < BIG_SCREEN_BORDER;
const IS_BIG_SCREEN = () => GET_CLIENT_WIDTH() > BIG_SCREEN_BORDER;

const LinkPreview: React.FC<LinkPreviewProps> = ({
  message: { domain, title, description, image, url, type, nickname, isFirstInGroup, isLastInGroup },
  renderParams: { imgParams },
  common: { messageViewType },
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const resizeHandlerRef = useRef<((e: UIEvent) => void) | undefined>(undefined);
  useEffect(() => {
    if (imageRef.current) {
      imageRef.current.style.display = 'none';
    }
  }, []);

  const shouldShowPreview = !!(title || description || image);
  if (!shouldShowPreview) return null;

  function calculateAndResizeImage(width: number, height: number): void {
    const ratio = width / height;
    let newWidth = width;
    let newHeight = height;
    const maxWidth = IS_SMALL_SCREEN() ? SMALL_WIDTH : (IS_BIG_SCREEN() ? BIG_WIDTH : MEDIUM_WIDTH);

    if (width > maxWidth || height > maxWidth) {
      const isWidthBigger = width > height;
      const isEven = width === height;

      newWidth = (isWidthBigger || isEven) ? maxWidth : (height * ratio);
      newHeight = isWidthBigger ? (maxWidth / ratio) : maxWidth;
    }

    if (imageRef.current) {
      imageRef.current.width = newWidth;
      imageRef.current.height = newHeight;
      imageRef.current.style.display = 'block';
    }
  }

  function handleImageLoaded(event: React.SyntheticEvent<HTMLImageElement>): void {
    const { width, height } = event.target as HTMLImageElement;
    if (width < MIN_WIDTH || height < MIN_HEIGHT) return;

    calculateAndResizeImage(width, height);

    if (resizeHandlerRef.current) {
      window.removeEventListener('resize', resizeHandlerRef.current);
    }

    const handleResize = (): void => {
      if (!imageRef.current) return;
      const { width: currentImageWidth } = imageRef.current;
      const shouldResizeImage =
        ((currentImageWidth === BIG_WIDTH) && !IS_BIG_SCREEN()) ||
        ((currentImageWidth === SMALL_WIDTH) && !IS_SMALL_SCREEN()) ||
        ((currentImageWidth === MEDIUM_WIDTH) && !IS_MEDIUM_SCREEN());

      if (shouldResizeImage) {
        calculateAndResizeImage(width, height);
      }
    }

    resizeHandlerRef.current = handleResize;
    window.addEventListener('resize', handleResize);
  }

  function handleOpenPreview(): void {
    window.open(url);
  }

  const className = composeClassName(type!, isFirstInGroup, !!isLastInGroup);

  return (
    <div className={`preview ${className}`}>
      {type === MessageTypes.OPERATOR && <AvatarImage {...{ messageViewType, imgParams, nickname }} />}
      <div className="message-content">
        <div className="message-text">
          <div className="link-preview">
            <img
              onClick={handleOpenPreview}
              ref={imageRef}
              onLoad={handleImageLoaded}
              src={image}
              role="button"
              alt={title}
              className="link-preview__image"
            />
            <div onClick={handleOpenPreview} className="link-preview__text-content" role="button">
              <span className="link-preview__title">{title}</span>
              <span className="link-preview__description">{description}</span>
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={url}
              className="link-preview__description"
            >
              {domain}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LinkPreview
